import { type AxiosError } from 'axios'
import { useFormContext, useWatch } from 'react-hook-form'
import { useIntl } from 'react-intl'

import {
  type SuccessReadUserOrdersV2,
  type UserOrderResponseWithFlags,
} from '@redteclab/api/clients/bully'

import { DIRECT_CONTACT_FORM_ORDERS_PER_REQUEST } from '../../direct-contact'
import { useApiOrdersSwrGetOrdersInfinite } from '../../order'
import { type DirectContactFormValues } from '../components/DirectContactForm/DirectContactFormModel'
import { directContactFormAccessorGetAvailabilityBySubject } from '../model/directContactFormAccessorGetAvailabilityBySubject'
import { directContactFormAccessorGetRecentOrders } from '../model/directContactFormAccessorGetRecentOrders'

import { useDirectContactFormLoadMoreOrderPages } from './useDirectContactFormLoadMoreOrderPages'
import { useDirectContactFormOrderPagination } from './useDirectContactFormOrderPagination'

const getVisibleOrders = (
  orders: UserOrderResponseWithFlags[],
  visibleSubOrderCount: number,
): UserOrderResponseWithFlags[] => {
  let remainingSubOrderCount = visibleSubOrderCount

  return orders.flatMap((order) => {
    const visibleSubOrders =
      order.subOrders?.slice(0, remainingSubOrderCount) ?? []

    remainingSubOrderCount -= visibleSubOrders.length

    return visibleSubOrders.length > 0
      ? // eslint-disable-next-line unicorn/no-unnecessary-array-flat-map -- don't reference, refactor if touched
        [{ ...order, subOrders: visibleSubOrders }]
      : []
  })
}

const getHasMoreApiPages = (
  responses: SuccessReadUserOrdersV2[] | undefined,
): boolean => {
  const lastResponse = responses?.at(-1)

  return (
    lastResponse?.page !== undefined &&
    lastResponse.totalPages !== undefined &&
    lastResponse.page + 1 < lastResponse.totalPages
  )
}

export const useDirectContactFormAvailableOrders = (): {
  availableOrders: UserOrderResponseWithFlags[]
  error: AxiosError | undefined
  hasMore: boolean
  isLoading: boolean
  isLoadingMore: boolean
  onLoadMore: () => void
} => {
  const intl = useIntl()
  const { control } = useFormContext<DirectContactFormValues>()
  const subject = useWatch({ control, name: 'subject' })
  const getAvailability = directContactFormAccessorGetAvailabilityBySubject(
    intl,
    subject,
  )
  const { data, error, isLoading, isValidating, setSize } =
    useApiOrdersSwrGetOrdersInfinite(DIRECT_CONTACT_FORM_ORDERS_PER_REQUEST)
  const { currentVisibleCount, onLoadMore } =
    useDirectContactFormOrderPagination(subject)
  const loadedOrders = data?.flatMap((response) => response.orders ?? []) ?? []
  const { hasReachedLookbackBoundary, recentOrders } =
    directContactFormAccessorGetRecentOrders(loadedOrders)

  const availableOrders = getAvailability
    ? recentOrders.flatMap((order) => {
        const availableSubOrders = order.subOrders?.filter(
          (subOrder) => getAvailability(subOrder) !== 'hidden',
        )

        return availableSubOrders?.length
          ? // eslint-disable-next-line unicorn/no-unnecessary-array-flat-map -- don't reference, refactor if touched
            [{ ...order, subOrders: availableSubOrders }]
          : []
      })
    : recentOrders

  const availableSubOrderCount = availableOrders.reduce(
    (count, order) => count + (order.subOrders?.length ?? 0),
    0,
  )

  const canLoadMoreApiPagesWithinDateRange =
    getHasMoreApiPages(data) && !hasReachedLookbackBoundary

  useDirectContactFormLoadMoreOrderPages({
    availableSubOrderCount,
    canLoadMoreApiPagesWithinDateRange,
    currentVisibleCount,
    isValidating,
    loadedPageCount: data?.length,
    setPageCount: setSize,
  })

  const visibleOrders = getVisibleOrders(availableOrders, currentVisibleCount)
  const isLoadingAvailableOrders =
    isLoading ||
    (visibleOrders.length === 0 &&
      (canLoadMoreApiPagesWithinDateRange || isValidating))

  return {
    availableOrders: visibleOrders,
    error,
    hasMore: currentVisibleCount < availableSubOrderCount,
    isLoading: isLoadingAvailableOrders,
    isLoadingMore: isValidating && data !== undefined,
    onLoadMore,
  }
}
